import { Close, ExpandMore } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Checkbox,
    Drawer,
    FormControlLabel,
    IconButton,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { BOTTOM_BAR_HEIGHT } from '/@/components/app-navbar/bottombar';
import { _ } from '/@/locale';

type ToggleOption = { value: string | number; label: string };

type MobileFiltersDrawerProps = {
    open: boolean;
    onClose: () => void;
    onApply: (filters: { types: (string | number)[]; phenologies: string[]; campaigns: string[] }) => void;
    trialTypesLoading: boolean;
    trialTypeOptions: ToggleOption[];
    phenologyOptions: ToggleOption[];
    campaignOptions: ToggleOption[];
    selectedTypes: (string | number)[];
    selectedPhenologies: string[];
    selectedCampaign: string[];
};

type FilterSectionKey = 'types' | 'phenologies' | 'campaigns';

const formatSelectedCount = (count: number) => _('{{count}} seleccionado', { count });

const FilterCheckboxList = memo(function FilterCheckboxList({
    options,
    value,
    onChange,
}: {
    options: ToggleOption[];
    value: (string | number)[];
    onChange: (next: (string | number)[]) => void;
}) {
    const valueSet = useMemo(() => new Set(value.map(String)), [value]);

    const toggle = useCallback(
        (optionValue: string | number) => {
            const key = String(optionValue);
            if (valueSet.has(key)) {
                onChange(value.filter((v) => String(v) !== key));
            } else {
                onChange([...value, optionValue]);
            }
        },
        [onChange, value, valueSet]
    );

    return (
        <Stack>
            {options.map((option, index) => {
                const checked = valueSet.has(String(option.value));
                return (
                    <FormControlLabel
                        key={String(option.value)}
                        control={
                            <Checkbox
                                checked={checked}
                                onChange={() => toggle(option.value)}
                                size='large'
                                color='secondary'
                            />
                        }
                        label={
                            <Typography variant='subtitle1' color='primary'>
                                {option.label}
                            </Typography>
                        }
                        sx={{
                            m: 0,
                            px: 2,
                            py: 1.5,
                            width: '100%',
                            borderBottom: (t) =>
                                index === options.length - 1 ? 'none' : `1px solid ${t.palette.divider}`,
                            '& .MuiFormControlLabel-label': { flex: 1 },
                        }}
                    />
                );
            })}
        </Stack>
    );
});

const MobileFiltersDrawer = memo(function MobileFiltersDrawer({
    open,
    onClose,
    onApply,
    trialTypesLoading,
    trialTypeOptions,
    phenologyOptions,
    campaignOptions,
    selectedTypes,
    selectedPhenologies,
    selectedCampaign,
}: MobileFiltersDrawerProps) {
    const [draftTypes, setDraftTypes] = useState(selectedTypes);
    const [draftPhenologies, setDraftPhenologies] = useState(selectedPhenologies);
    const [draftCampaigns, setDraftCampaigns] = useState(selectedCampaign);
    const [expanded, setExpanded] = useState<FilterSectionKey | false>(false);

    useEffect(() => {
        if (!open) return;
        setDraftTypes(selectedTypes);
        setDraftPhenologies(selectedPhenologies);
        setDraftCampaigns(selectedCampaign);
        setExpanded(false);
    }, [open, selectedTypes, selectedPhenologies, selectedCampaign]);

    // Clearing leaves every group empty, which the map reads as "show all".
    const handleClear = useCallback(() => {
        setDraftTypes([]);
        setDraftPhenologies([]);
        setDraftCampaigns([]);
    }, []);

    const handleApply = useCallback(() => {
        onApply({
            types: draftTypes,
            phenologies: draftPhenologies,
            campaigns: draftCampaigns,
        });
        onClose();
    }, [draftTypes, draftPhenologies, draftCampaigns, onApply, onClose]);

    const sectionTitle = (label: string, count: number) =>
        count > 0 ? `${label} (${formatSelectedCount(count)})` : label;

    return (
        <Drawer
            anchor='bottom'
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    bottom: BOTTOM_BAR_HEIGHT,
                    maxHeight: `calc(70vh - ${BOTTOM_BAR_HEIGHT})`,
                    borderRadius: 0,
                    boxShadow: 'none',
                },
            }}
            hideBackdrop
        >
            <Stack height='100%' maxHeight='inherit'>
                <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    pl={2}
                    pr={1}
                    py={1}
                    borderBottom={(t) => `1px solid ${t.palette.divider}`}
                >
                    <Typography variant='subtitle1' color='primary'>
                        {_('Filtros')}
                    </Typography>
                    <IconButton onClick={onClose} size='large' aria-label={_('Cerrar')}>
                        <Close />
                    </IconButton>
                </Stack>

                <Stack flex={1} overflow='auto' py={2} px={2} spacing={0.5}>
                    <Accordion
                        disableGutters
                        elevation={0}
                        expanded={expanded === 'types'}
                        onChange={(_, isExpanded) => setExpanded(isExpanded ? 'types' : false)}
                        sx={{
                            borderBottom: (t) => `1px solid ${t.palette.divider}`,
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMore color='action' />}>
                            <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                                {sectionTitle(_('Tipo de ensayo'), draftTypes.length)}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            {trialTypesLoading ? (
                                <Skeleton variant='rectangular' width='100%' height={60} />
                            ) : (
                                <FilterCheckboxList
                                    options={trialTypeOptions}
                                    value={draftTypes}
                                    onChange={setDraftTypes}
                                />
                            )}
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        disableGutters
                        elevation={0}
                        expanded={expanded === 'phenologies'}
                        onChange={(_, isExpanded) => setExpanded(isExpanded ? 'phenologies' : false)}
                        sx={{
                            borderBottom: (t) => `1px solid ${t.palette.divider}`,
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMore color='action' />}>
                            <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                                {sectionTitle(_('Estadio fenológico'), draftPhenologies.length)}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            <FilterCheckboxList
                                options={phenologyOptions}
                                value={draftPhenologies}
                                onChange={(next) => setDraftPhenologies(next.map(String))}
                            />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        disableGutters
                        elevation={0}
                        expanded={expanded === 'campaigns'}
                        onChange={(_, isExpanded) => setExpanded(isExpanded ? 'campaigns' : false)}
                        sx={{
                            borderBottom: (t) => `1px solid ${t.palette.divider}`,
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMore color='action' />}>
                            <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                                {sectionTitle(_('Año'), draftCampaigns.length)}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            <FilterCheckboxList
                                options={campaignOptions}
                                value={draftCampaigns}
                                onChange={(next) => setDraftCampaigns(next.map(String))}
                            />
                        </AccordionDetails>
                    </Accordion>
                </Stack>

                <Stack
                    direction='row'
                    justifyContent='flex-end'
                    alignItems='center'
                    gap={2}
                    px={2}
                    py={2}
                    borderTop={(t) => `1px solid ${t.palette.divider}`}
                >
                    <Button variant='text' color='primary' onClick={handleClear}>
                        {_('Limpiar')}
                    </Button>
                    <Button variant='contained' color='primary' onClick={handleApply}>
                        {_('Aplicar')}
                    </Button>
                </Stack>
            </Stack>
        </Drawer>
    );
});

export default MobileFiltersDrawer;
